/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	INameValuePair
} from '@shared/interfaces/application-objects/name-value-pair.interface';
import {
	differenceBy,
	get
} from 'lodash-es';

/**
 * A class containing static helper methods for the
 * object array type.
 *
 * @export
 * @class ObjectArrayHelper
 */
export class ObjectArrayHelper
{
	/**
	 * Returns an array of comma separated property values for
	 * object arrays.
	 *
	 * @static
	 * @param {object[]} array
	 * The array to be evaluated.
	 * @param {string} propertyName
	 * The property value to concatenate.
	 * @memberof ObjectArrayHelper
	 * @returns {string}
	 * A comma separated array of property values.
	 */
	public static commaSeparatedPropertyValues(
		array: object[],
		propertyName: string): string
	{
		return array
			.filter((item: object) =>
				!AnyHelper.isNullOrEmpty(item[propertyName]))
			.map((item: object) => item[propertyName])
			.join(AppConstants.characters.comma);
	}

	/**
	 * Returns a consolidated list of the differences
	 * between two arrays.
	 *
	 * @static
	 * @param {object[]} initialArray
	 * The array to be evaluated.
	 * @param {object[]} comparisonArray
	 * The list of data to check against.
	 * @param {string} dataKey
	 * The data key if any existing.
	 * @memberof ObjectArrayHelper
	 * @returns {object[]}
	 * The object array diferences.
	 */
	public static getObjectArrayDifferences(
		initialArray: object[],
		comparisonArray: object[],
		dataKey: string): object[]
	{
		return differenceBy(
			initialArray,
			comparisonArray,
			dataKey)
			.concat(
				differenceBy(
					comparisonArray,
					initialArray,
					dataKey));
	}

	/**
	 * Returns an object from an array based on a property value.
	 * @remarks Nested property lookups are supported.
	 *
	 * @static
	 * @param {object[]} array
	 * The array to be evaluated.
	 * @param {string} propertyName
	 * The property to be evaluated in each array item.
	 * @param {any} matchValue
	 * The value to compare to find a match for.
	 * @returns {object}
	 * The object matching the property value.
	 * @typeparam TReturnType
	 * The object type to return.
	 * @memberof ObjectArrayHelper
	 */
	public static findByProperty<TReturnType>(
		array: object[],
		propertyName: string,
		matchValue: any): TReturnType
	{
		if (AnyHelper.isNull(array))
		{
			return null;
		}

		return array.find(
			(item: object) =>
				get(item, propertyName) ===	matchValue) as TReturnType
				?? null;
	}

	/**
	 * Returns a grouped array of objects from an array based on a
	 * pivot property value.
	 *
	 * @param {object[]} array
	 * The array to group by the property name.
	 * @param {string} propertyName
	 * The property name to group by.
	 * @returns {pivotProperty: string; items: any[]}
	 * The grouped array of objects.
	 * @memberof ObjectArrayHelper
	 */
	public static groupByProperty(
		array: object[],
		propertyName: string): {pivotProperty: string; items: any[]}[]
	{
		const groupedByProperty: any =
			array.reduce(
				(accumulator: any,
					item: object) =>
				{
					const propertyValue: string =
						get(item, propertyName)?.toString();

					if (AnyHelper.isNull(accumulator[propertyValue]))
					{
						accumulator[propertyValue] =
							{
								pivotProperty: propertyValue,
								items: []
							};
					}

					accumulator[propertyValue]
						.items
						.push(item);

					return accumulator;
				},
				{});

		return Object.values(groupedByProperty);
	}

	/**
	 * Returns the value of a setting from a name-value pairs array
	 * matching the given name, or undefined if not found.
	 *
	 * @static
	 * @param {INameValuePair[]} settings
	 * The name-value pair settings array to search.
	 * @param {string} settingName
	 * The setting name to look up.
	 * @returns {string | null}
	 * The matching setting value, or null if not found.
	 * @memberof ObjectArrayHelper
	 */
	public static findSettingValue(
		settings: INameValuePair[],
		settingName: string): string | null
	{
		return ObjectArrayHelper
			.findByProperty<INameValuePair>(
				settings,
				AppConstants.commonProperties.name,
				settingName)
			?.value ?? null;
	}
}